import { Trash2 } from 'lucide-react';

import { SPAN_TYPES, TAG_META, fieldFor, operationFor, spanProblem } from '../../lib/spans.js';
import { Keycap } from '../common/index.jsx';
import { SpanOutcome } from './SpanOutcome.jsx';

/**
 * The list of tags on the focused caption, with their operation, replacement and note.
 *
 * Inputs are uncontrolled-ish: they update the store on change but are keyed by span
 * identity, so typing a note does not re-render the caption surface.
 */
export function SpanRail({
  spans,
  cursor,
  onCursor,
  onUpdate,
  onRetag,
  onRemove,
  readOnly = false,
}) {
  if (!spans.length) {
    return (
      <div className="span-rail-empty">
        No tags yet. Select text in the caption, then press{' '}
        <Keycap>h</Keycap> <Keycap>u</Keycap> <Keycap>w</Keycap>.
      </div>
    );
  }

  return (
    <div className="span-rail">
      {spans.map((span, index) => {
        const meta = TAG_META[span.label] || {};
        const field = fieldFor(span.label);
        const operation = operationFor(span.label, span);
        const problem = spanProblem(span);

        return (
          <div
            key={`${span.start_char}-${span.end_char}-${span.label}`}
            className={[
              'span-row',
              `label-${span.label}`,
              index === cursor ? 'cursor' : '',
              problem ? 'incomplete' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onCursor?.(index)}
          >
            <div className="span-row-head">
              <span className={`chip ${span.label}`}>{meta.short || '?'}</span>
              <span className="span-quote" title={span.text}>
                <em>{span.text}</em>
              </span>
              {!readOnly && (
                <button
                  type="button"
                  className="btn ghost icon"
                  aria-label={`Remove the ${meta.label} tag on "${span.text}"`}
                  onClick={() => onRemove(index)}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>

            {!readOnly && (
              <div className="span-row-controls">
                <select
                  className="select sm"
                  value={span.label}
                  aria-label="Tag type"
                  onChange={(e) => onRetag(index, e.target.value)}
                >
                  {SPAN_TYPES.map((label) => (
                    <option key={label} value={label}>
                      {TAG_META[label].label}
                    </option>
                  ))}
                </select>

                {field ? (
                  <input
                    className="input sm"
                    placeholder={field.placeholder}
                    value={span[field.key] || ''}
                    aria-label={field.label}
                    onChange={(e) => onUpdate(index, { [field.key]: e.target.value })}
                  />
                ) : (
                  <span className="tiny faint">the hedge is removed</span>
                )}


              </div>
            )}

            {!readOnly && <SpanOutcome span={span} compact />}
            {problem && <span className="span-problem">{problem}</span>}
          </div>
        );
      })}
    </div>
  );
}

export function TagLegend({ showAdded = true }) {
  return (
    <div className="legend">
      {SPAN_TYPES.map((label) => (
        <span key={label} className="legend-item">
          <span
            className="legend-swatch"
            style={{ background: `var(--tag-${label === 'wrong_uncertainty' ? 'wrong' : label}-bg)` }}
          />
          {TAG_META[label].label}
        </span>
      ))}
      {showAdded && (
        <span className="legend-item">
          <span className="legend-swatch" style={{ background: 'var(--tag-added-bg)' }} />
          Text you added
        </span>
      )}
    </div>
  );
}
